import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "../../design/design-system";
import Text from "../../atoms/Text";
import Button from "../../atoms/Button";
import Icon from "../../atoms/Icon";

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${(props) => (props.isOpen ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[100]};
  }
`;

const ReferenceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ReferenceItem = styled.div`
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
`;

const ReferenceTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const SnuLogo = styled.img`
  height: 18px;
  width: auto;
`;

const ColorDot = styled.div<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
`;

const ReferenceDescription = styled.div`
  font-size: 13px;
  line-height: 1.6;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const ReferenceLink = styled.a`
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Skyblue")?.[500]};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`;

interface ReferenceInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const referenceItems = [
  {
    title: "허가사항",
    color: COLOR_SYSTEM.get("Blue")?.[500],
    description:
      "식품의약품안전처에서 허가한 의약품의 공식 정보입니다. 효능·효과, 용법·용량, 사용상 주의사항, 상호작용 등 의약품 사용에 필요한 핵심 정보를 포함합니다.",
  },
  {
    title: "SPST v3",
    color: COLOR_SYSTEM.get("Green")?.[500],
    description:
      "STOPP/START criteria version 3 - 유럽 노인병학회 주도로 개발되어 전 세계적으로 통용되는 노인 처방 가이드라인의 최신 개정판(2023)입니다. 노인 환자에게 임상적 위험이 유익성보다 클 수 있는 약물(STOPP) 뿐만 아니라, 질병 치료를 위해 처방이 권장되나 누락된 약물(START)까지 양방향으로 스크리닝하여 처방 적절성을 평가합니다.",
    url: "https://www.cgakit.com/stopp-start-v3",
  },
  {
    title: "다제",
    color: COLOR_SYSTEM.get("Purple")?.[500],
    description:
      "다제약물 관리사업 필수 체크리스트 - 국민건강보험공단이 주관하는 다제약물 관리사업의 자문 약사들이 사용하는 실무 가이드라인에서 체크해야할 약물리스트를 발췌하였습니다. 다약제 복용 환자를 대상으로 반드시 확인해야 하는 복약이행도 체크약물과 고위험 약품목록, 교육자료 리스트가 있습니다.",
  },
  {
    title: "KABS",
    color: COLOR_SYSTEM.get("Skyblue")?.[500],
    description:
      "Korean Anticholinergic Burden Scale - [서울대학교 약학대학 임상약학대학원 이주연 교수팀 개발] 국내 의료 환경에 최적화된 한국형 항콜린 약물 부담 척도입니다. 기존에 존재하는 10개의 국외 항콜린 척도 도구를 체계적으로 문헌 고찰하고, 전문가 델파이 합의를 거쳐 개발되었습니다. 국내 유통되는 약물 중 항콜린 작용이 있는 약물을 선별하여 강도에 따라 1점(약함)부터 3점(강함)까지 점수화했습니다. 노인 환자의 다제약물 복용 시 누적되는 항콜린 부담을 정량적으로 평가하는 데 활용됩니다. (Geriatrics & Gerontology International 게재)",
    url: "https://onlinelibrary.wiley.com/doi/10.1111/ggi.13680",
    showSnuLogo: true,
  },
  {
    title: "CCMM",
    color: COLOR_SYSTEM.get("Purple")?.[500],
    description:
      "Community Comprehensive Medication Management - [서울대학교 약학대학 임상약학대학원 이주연 교수팀 개발] 지역사회 거주 노인을 대상으로 약물 사용의 적절성을 평가하는 도구입니다. Beers, GheOP3S 등 전 세계 주요 도구를 분석하고 국내 임상 전문가 및 지역 약사들의 타당성 검토를 거쳐 완성되었습니다. 5개 영역(부적절 약물, 상호작용, 질병 금기, 용량 주의, 처방 누락)과 23개 기저질환군을 반영하여 정밀한 검토를 제공합니다. (Journal of Public Health 게재)",
    url: "https://academic.oup.com/jpubhealth/article/45/3/645/7076575?login=false",
    showSnuLogo: true,
  },
  {
    title: "LCMM",
    color: COLOR_SYSTEM.get("Purple")?.[500],
    description:
      "Long-term care Comprehensive Medication Management - [서울대학교 약학대학 임상약학대학원 이주연 교수팀 개발] 국내 요양시설 거주 노인의 특성에 맞춰 개발된 한국형 약물 검토 도구입니다. Beers Criteria, STOPP/START 등 21종의 문헌 고찰과 델파이 합의를 거쳐, 요양시설 환경에서 빈번한 약물 문제(PIM, DDI 등)를 효과적으로 스크리닝하도록 설계되었습니다. (Therapeutic Advances in Chronic Disease 게재)",
    url: "https://journals.sagepub.com/doi/10.1177/20406223221128444",
    showSnuLogo: true,
  },
  {
    title: "안전원",
    color: COLOR_SYSTEM.get("Yellow")?.[500],
    description:
      "한국의약품안전관리원 노인 의약품 적정 사용 정보 - 안전관리원에서는 취약 환자의 보다 안전한 의약품 사용을 위하여 취약군별 '의약품 적정사용정보집'을 제공하고 있습니다. 노인의 약동학 및 약력학적 특수성과 처방·조제 시의 일반 원칙(다제약물, 상호작용 등)을 확인할 수 있어 노인 환자의 생리학적 변화에 대한 포괄적인 이해를 돕습니다. 허가사항과 국내외 노인주의 의약품 기준들을 검토하여 동일계열의 국내 허가 성분들을 추출하여 노인에게 부적절할 수 있는 총 211개 성분이 선별되어 있으며, 이를 근골격계, 신경정신계 등 주요 효능군별로 분류하여 제시합니다.",
    url: "https://www.drugsafe.or.kr/iwt/ds/ko/useinfo/EgovDurInfoUseOld.do",
  },
];

const ReferenceInfoModal: React.FC<ReferenceInfoModalProps> = ({ isOpen, onClose }) => {
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <Text textType="H2" color={COLOR_SYSTEM.get("Gray")?.[800]}>
            근거자료 안내
          </Text>
          <CloseButton onClick={onClose}>
            <Icon name="X" size={20} color={COLOR_SYSTEM.get("Gray")?.[500]} />
          </CloseButton>
        </ModalHeader>

        <Text
          textType="Body2"
          color={COLOR_SYSTEM.get("Gray")?.[600]}
          style={{ marginBottom: "20px" }}
        >
          AI 분석 결과의 근거가 되는 자료들입니다. 각 탭을 클릭하여 상세 내용을 확인하실 수있습니다.
        </Text>

        <ReferenceList>
          {referenceItems.map((item) => (
            <ReferenceItem key={item.title}>
              <ReferenceTitle>
                <ColorDot color={item.color || COLOR_SYSTEM.get("Gray")?.[500]} />
                <Text textType="Body1Bold" color={COLOR_SYSTEM.get("Gray")?.[800]}>
                  {item.title}
                </Text>
                {item.showSnuLogo && (
                  <SnuLogo src="/images/snu-logo_nomargin.png" alt="서울대학교" />
                )}
              </ReferenceTitle>
              <ReferenceDescription>{item.description}</ReferenceDescription>
              {item.url && (
                <ReferenceLink href={item.url} target="_blank" rel="noopener noreferrer">
                  원문 보기
                </ReferenceLink>
              )}
            </ReferenceItem>
          ))}
        </ReferenceList>

        <ButtonContainer>
          <Button
            buttonType="button"
            type="Text"
            variant="Contained"
            size="Medium"
            color="Primary"
            onClick={onClose}
          >
            확인
          </Button>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ReferenceInfoModal;
